import { keepPreviousData, useQuery } from '@tanstack/react-query'
import axios from 'axios'

import { CacheKeys } from '../lib/cache'
import { toCamelCase } from '../lib/typeHelper'
import { type SalesLeadActivity } from './salesLead'
import { type StaffUser } from './user'
import { type Reminder } from './userReminder'

/** The staff user page's tabs, as held in its `?tab=` param. */
export enum StaffUserTab {
  ACTIVITY = 'activity',
  CUSTOMERS = 'customers',
  LEADS = 'leads',
  REMINDERS = 'reminders',
}

/** The activity feed's filters — the backend's `kind` values. */
export enum StaffActivityKind {
  ALL = 'all',
  CALLS = 'calls',
  EMAILS = 'emails',
  SMS = 'sms',
  NOTES = 'notes',
  STATUS = 'status',
}

export interface StaffUserDetail extends StaffUser {
  counts: {
    customers: number
    salesLeads: number
    /** Open reminders in the My Reminders window. */
    reminders: number
  }
  activityCounts: Record<StaffActivityKind, number>
}

/** A lead timeline row plus the lead it happened on. */
export interface StaffActivity extends SalesLeadActivity {
  lead: { id: string; name: string }
}

interface StaffActivityPage {
  count: number
  next: string | null
  previous: string | null
  results: StaffActivity[]
}

export function staffUserHref(id: string): string {
  return `/insure/internal/staff-users/${id}`
}

function getStaffUser(id: string): Promise<StaffUserDetail> {
  return axios
    .get(`/api/v1/insure/internal/staff-users/${id}/`)
    .then((response) => toCamelCase(response.data) as StaffUserDetail)
}

export function useStaffUser(id: string) {
  const { data, isPending, isError } = useQuery({
    queryKey: [CacheKeys.STAFF_USER, id],
    queryFn: () => getStaffUser(id),
    enabled: !!id,
  })
  return { staffUser: data, isPending, isError }
}

function getStaffUserActivity(id: string, kind: StaffActivityKind, page: number): Promise<StaffActivityPage> {
  const params: Record<string, string> = {}
  if (kind !== StaffActivityKind.ALL) params.kind = kind
  if (page > 1) params.page = String(page)
  return axios
    .get(`/api/v1/insure/internal/staff-users/${id}/activity/`, { params })
    .then((response) => toCamelCase(response.data) as StaffActivityPage)
}

export function useStaffUserActivity(id: string, kind: StaffActivityKind, page: number) {
  const { data, isPending, isError, isFetching } = useQuery({
    queryKey: [CacheKeys.STAFF_USER_ACTIVITY, id, kind, page],
    queryFn: () => getStaffUserActivity(id, kind, page),
    placeholderData: keepPreviousData,
  })
  return {
    activities: data?.results ?? [],
    count: data?.count ?? 0,
    hasNextPage: !!data?.next,
    hasPreviousPage: !!data?.previous,
    isPending,
    isError,
    isFetching,
  }
}

function getStaffUserReminders(id: string): Promise<Reminder[]> {
  return axios
    .get(`/api/v1/insure/internal/staff-users/${id}/reminders/`)
    .then((response) => toCamelCase(response.data) as Reminder[])
}

export function useStaffUserReminders(id: string) {
  const { data, isPending, isError } = useQuery({
    queryKey: [CacheKeys.STAFF_USER_REMINDERS, id],
    queryFn: () => getStaffUserReminders(id),
  })
  return { reminders: data ?? [], isPending, isError }
}
